import { type ImageLoader } from 'next/image';
import { NEXT_STATIC_IMAGES } from '../constants';
import captureException from './captureException';

/**
 * Base64 encoded transparent 1x1 gif
 * Used as a fallback in the case of an invalid image.
 * The rationale is that an empty image is better than an image with a broken src.
 *
 * @private Only exported for use in tests
 */
export const TRANSPARENT_GIF = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * Returns the image src with the image params appended to the src.
 *
 * - Any existing params on the `src` will be removed.
 * - Adds params to the src.
 * - Next assets will be prefixed with the asset domain.
 * - On the dev environment, next assets will served locally.
 * -
 *
 * @param src The src of the image.
 * @param params Image params to be appended to the src.
 * @returns
 */
export function buildImageSrc(src: string, params: ImageParams): string {
  if (!src || src.startsWith('//') || !['/', 'https://'].some((prefix) => src.startsWith(prefix))) {
    if (src) {
      captureException(new Error('Invalid image src'), { extra: { src } });
    }
    return TRANSPARENT_GIF;
  }

  // Use the default image domain if the src doesn't include the domain.
  let newSrc = src.startsWith('/') ? `https://${process.env.NEXT_PUBLIC_IMAGES_CDN}` : '';
  // Prefix static assets images with the appropriate domain.
  if (src.startsWith(NEXT_STATIC_IMAGES)) {
    // assets in the dev environment will be served the local file.
    newSrc = process.env.NEXT_PUBLIC_ASSETS_CDN || '';
  }

  newSrc += src.split('?')[0];

  const query = new URLSearchParams();
  // Order of query params should be deterministic and preserved to increase CDN cache hits.
  ['q', 'fit', 'w', 'h', 'dpr'].forEach((key) => {
    if (params[key]) {
      query.set(key, params[key]);
    }
  });

  const queryString = query.toString();
  return newSrc + (queryString ? `?${queryString}` : '');
}

/**
 * Returns a srcset with sizes for 1x and 2x pixel densities.
 *
 * @param src The src of the image.
 * @param params Image params.
 * @returns
 */
export function buildSrcSet(src: string, params: ImageParams): string {
  return [1, 2].map((dpr) => `${buildImageSrc(src, { ...params, dpr })} ${dpr}x`).join(', ');
}

/**
 * Returns an array of srcset entries based on the provided srcset params.
 * Each srcset param set can have a unique `src`, which allows for art direction
 * depending on the available space.
 *
 * @param src The src of the image.
 * @param srcSetParams Array of srcset params. Each entry can have it's own src.
 * @param params Image params that will be merged with srcset params.
 * @returns
 */
export function buildSrcSets(
  src: string,
  srcSetParams: SrcSetParam[],
  params: ImageParams = {},
): string[] {
  return srcSetParams.map(({ w, h, imgSrc }) => buildSrcSet(imgSrc ?? src, { ...params, w, h }));
}

export type ImageParams = {
  /**
   * Image width
   */
  w?: number;
  /**
   * Image height
   */
  h?: number;
  /**
   * The fit parameter controls how the output image is fit to its target
   * dimensions after resizing, and how any background areas will be filled.
   * - `crop`: Resizes the image to fill the width and height dimensions and
   *    crops any excess image data. The resulting image will match the width
   *    and height constraints without distorting the image. Both the `w` and
   *    `h` parameters will also need to be set.
   * - `max`: Resizes the image to fit within the width and height dimensions
   *    without cropping or distorting the image, but will not increase the
   *    size of the image if it is smaller than the output size. The resulting
   *    image will maintain the same aspect ratio of the input image.
   */
  fit?: 'crop' | 'max';
  /**
   * The device pixel ratio which will scale the pixel density in the requested image.
   */
  dpr?: number;
  /**
   * Output Quality (0 - 100) default: 75
   * @deprecated Support for quality will be removed in favor of having fewer image variants
   */
  q?: number;
};

export type SrcSetParam = {
  w: number;
  h?: number;
  query: string;
  imgSrc?: string;
};

export const relativeLoader: ImageLoader = ({ src, width }) => buildImageSrc(src, { w: width });
